import React, { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { useAppDispatch, useAppSelector } from '../hooks/index';
import { setToken, setRole, setLoading, setError } from '../store/authSlice';
import { auth } from '../services/api';

const SignupPage: React.FC = () => {
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        confirmPassword: '',
        name: '',
    });
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { loading, error } = useAppSelector((state) => state.auth);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (formData.password !== formData.confirmPassword) {
            dispatch(setError('Passwords do not match'));
            return;
        }

        try {
            dispatch(setLoading(true));
            dispatch(setError(null));

            // Register the user
            await auth.signup(formData.username, formData.password, formData.name);

            // After successful registration, log the user in
            const loginResponse = await auth.login(formData.username, formData.password);

            dispatch(setToken(loginResponse.access_token));
            dispatch(setRole(loginResponse.role));
            navigate('/dashboard');
        } catch (err: any) {
            dispatch(setError(err.response?.data?.message || 'An error occurred during signup'));
        } finally {
            dispatch(setLoading(false));
        }
    }, [formData, dispatch, navigate]);

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
            {/* Top Navigation Bar */}
            <TopBar />
            <div className="flex flex-1 items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative">
                <div className="absolute left-0 top-0 mt-20 ml-6">
                    <Link to="/" className="inline-flex items-center px-4 py-2 bg-white border border-primary-100 rounded-lg shadow hover:bg-primary-50 text-primary-600 font-poppins font-medium transition-colors">
                        <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                        Back to Home
                    </Link>
                </div>
                <div className="max-w-md w-full">
                    <div className="text-center mb-12">
                        <h1 className="font-playfair text-4xl font-bold text-primary-600 mb-4">
                            Create Account
                        </h1>
                        <p className="font-poppins text-gray-600 text-lg">
                            Start creating viral content today
                        </p>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-8">
                        <form className="space-y-6" onSubmit={handleSubmit}>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="email" className="block font-poppins font-medium text-gray-900 mb-2">
                                    Email address
                                </label>
                                <input
                                    id="email"
                                    name="username"
                                    type="email"
                                    required
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 font-poppins focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent transition duration-300"
                                    placeholder="Enter your email"
                                    value={formData.username}
                                    onChange={handleChange}
                                />
                            </div>
                            <div>
                                <label htmlFor="password" className="block font-poppins font-medium text-gray-900 mb-2">
                                    Password
                                </label>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    required
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 font-poppins focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent transition duration-300"
                                    placeholder="Enter your password"
                                    value={formData.password}
                                    onChange={handleChange}
                                />
                            </div>
                            <div>
                                <label htmlFor="confirmPassword" className="block font-poppins font-medium text-gray-900 mb-2">
                                    Confirm Password
                                </label>
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type="password"
                                    required
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 font-poppins focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent transition duration-300"
                                    placeholder="Confirm your password"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                />
                            </div>
                            <div>
                                <label htmlFor="name" className="block font-poppins font-medium text-gray-900 mb-2">
                                    Full Name
                                </label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    required
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 font-poppins focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent transition duration-300"
                                    placeholder="Enter your full name"
                                    value={formData.name}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="text-red-600 text-sm text-center font-poppins mt-4 p-3 bg-red-50 rounded-lg">
                                {error}
                            </div>
                        )}

                        <div className="mt-6">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-3 px-4 rounded-lg font-poppins font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600 transform transition duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <span className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Creating account...
                                    </span>
                                ) : (
                                    'Create account'
                                )}
                            </button>
                        </div>
                    </form>
                    </div>
                    <div className="text-center mt-8">
                        <p className="font-poppins text-gray-600">
                            Already have an account?{' '}
                            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium transition duration-300">
                                Sign in
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default SignupPage;
